import React, { useMemo, useState } from 'react';
import TextField from '@mui/material/TextField';
import StbButton from '../security-toolbox-ui/StbButton';
import {
  useAttachLabScenarioItems,
  useCompileLabScenario,
  useDetachLabScenarioItem,
  useLabConfigurationItems,
  useLabScenarioItems,
  useSaveLabRow,
} from '../../hooks/lab-hooks';
import {
  LabCompileResult,
  LabConfigurationItem,
  LabItemAttachPair,
  LabItemAttachResult,
  LabItemDetachResult,
  LabDevice,
  LabScenarioItem,
  labEffectiveOptions,
  labItemBlockers,
  labItemOperationLabel,
  LAB_TABLES,
} from '../../support/labService';
import { LAB_JSON_SCHEMAS } from '../../support/labSpecSchema';
import {
  LabCard,
  LabCheckbox,
  LabJsonView,
  LabPanelState,
  LabRowSelect,
  LabStatusTag,
  labErrorMessage,
} from './LabShared';
import { LabSpecBuilder } from './LabSpecFields';

export type LabScenarioItemsPanelProps = {
  /** The scenario these items are attached to. Nothing loads without one. */
  scenarioId?: number;
  scenarioName?: string;
  /**
   * The scenario's devices, nested on the scenario row.
   *
   * Passed in rather than fetched: the parent already has the whole scenario, and an attachment is
   * "this item, on this device" - with no devices there is nothing to attach anything to, which is
   * a thing this panel has to be able to say.
   */
  devices?: LabDevice[];
  /** Authoring. The API refuses either way; this decides whether the buttons are there. */
  canEdit?: boolean;
};

/** One line of the attach form: an item, and the device it is going on. */
type LabAttachChoice = {
  item: number;
  /** Null takes the batch device. Set only when this item goes somewhere else. */
  device: number | null;
};

/**
 * Every item the chosen ones drag in with them.
 *
 * Computed here only to say so before the attach happens - the server walks the same graph and
 * its answer is the one that is written. Showing it first is the difference between choosing one
 * item and being surprised by eleven rows, and choosing one item knowing it needs ten others.
 */
const prerequisiteClosure = (
  chosen: number[],
  byId: Map<number, LabConfigurationItem>,
): number[] => {
  const seen = new Set<number>(chosen);
  const extra: number[] = [];
  const queue = [...chosen];

  while (queue.length) {
    const current = byId.get(queue.shift() as number);

    (current?.prerequisites || []).forEach((id) => {
      if (seen.has(id)) return;

      seen.add(id);
      extra.push(id);
      queue.push(id);
    });
  }

  return extra;
};

/**
 * What one scenario is made of, and turning that into work an agent can lease.
 *
 * Two things are on this screen because they come apart: the item list is what an author decided,
 * and the steps are what the last compile produced from it. A list edited after the last compile
 * is a lab that will run yesterday's configuration, and nothing else in the UI can show that.
 *
 * Attaching is one request for however many items are chosen, prerequisites included. Detaching
 * takes the prerequisites nothing else needs with it, and says which - otherwise a scenario
 * silently accumulates rows nobody chose and nobody can tell apart from deliberate ones.
 *
 * Every attachment names a device, because "this item, on this box" is what one is. The form takes
 * one device for the batch and lets a row differ, which is the shape the work actually has: eight
 * items onto the firewall and one onto the jump host, not nine separate decisions. The same item can
 * be attached twice against two devices - that is how one hardening item covers three hosts.
 */
const LabScenarioItemsPanel: React.FC<LabScenarioItemsPanelProps> = ({
  scenarioId,
  scenarioName,
  devices = [],
  canEdit = false,
}) => {
  const [attachOpen, setAttachOpen] = useState(false);
  const [chosen, setChosen] = useState<LabAttachChoice[]>([]);
  const [batchDevice, setBatchDevice] = useState<number | null>(null);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [overrideDraft, setOverrideDraft] = useState<Partial<LabScenarioItem>>({});
  const [overrideError, setOverrideError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [attachResult, setAttachResult] = useState<LabItemAttachResult | null>(null);
  const [detachResult, setDetachResult] = useState<LabItemDetachResult | null>(null);
  const [compileResult, setCompileResult] = useState<LabCompileResult | null>(null);

  const { data: attachments, isLoading, error } = useLabScenarioItems(scenarioId);
  const { data: catalogue } = useLabConfigurationItems();

  const attach = useAttachLabScenarioItems();
  const detach = useDetachLabScenarioItem();
  const compile = useCompileLabScenario();
  const saveAttachment = useSaveLabRow<Partial<LabScenarioItem> & { id?: number }>(
    LAB_TABLES.scenarioItem,
  );

  // Memoised so the lookup map and the closure preview below are not rebuilt on every render.
  const rows = useMemo(() => attachments || [], [attachments]);
  const items = useMemo(() => catalogue || [], [catalogue]);

  const byId = useMemo(
    () => new Map(items.map((item) => [item.id, item] as [number, LabConfigurationItem])),
    [items],
  );

  const sortedDevices = useMemo(
    () =>
      [...devices].sort(
        (a, b) => (a.order || 0) - (b.order || 0) || a.device_key.localeCompare(b.device_key),
      ),
    [devices],
  );

  const deviceById = useMemo(
    () => new Map(sortedDevices.map((device) => [device.id, device] as [number, LabDevice])),
    [sortedDevices],
  );

  /**
   * What is already here, keyed on the pair rather than the item.
   *
   * The unique thing is (item, device): one hardening item legitimately appears three times against
   * three hosts, so keying on the item alone would hide two thirds of the list and refuse the rest.
   */
  const attachedPairs = useMemo(
    () => new Set(rows.map((row) => `${row.item}:${row.device}`)),
    [rows],
  );

  /** Which devices each item is already on, so the form can say so before a duplicate is chosen. */
  const attachedDevicesByItem = useMemo(() => {
    const map = new Map<number, string[]>();

    rows.forEach((row) => {
      const label = row.device_key || deviceById.get(row.device)?.device_key || `device ${row.device}`;

      map.set(row.item, [...(map.get(row.item) || []), label]);
    });

    return map;
  }, [rows, deviceById]);

  /**
   * The whole catalogue, not the catalogue minus what is attached.
   *
   * An item already on one device is a legitimate choice for a second one. What is refused is the
   * exact pair, and that is checked below where the device is known.
   */
  const available = items;

  const chosenIds = useMemo(() => chosen.map((choice) => choice.item), [chosen]);

  /** What the chosen items will bring in that nobody picked. Each inherits its requirer's device. */
  const impliedIds = useMemo(
    () => prerequisiteClosure(chosenIds, byId),
    [chosenIds, byId],
  );

  /**
   * Pairs already attached, filtered out rather than refused.
   *
   * A batch assembled by ticking rows can easily include one that is already on the batch device -
   * that is not a mistake worth stopping for, and the server treats a repeat as one attachment too.
   */
  const alreadyThere = useMemo(
    () =>
      chosen.filter((choice) => {
        const device = choice.device ?? batchDevice;

        return device !== null && attachedPairs.has(`${choice.item}:${device}`);
      }),
    [chosen, batchDevice, attachedPairs],
  );

  /**
   * Why the attach cannot be sent yet.
   *
   * Named here rather than left to the server, which refuses the same things: a 400 on a batch of
   * thirty says "invalid" and not "the third row is a firewall item on a Windows host".
   */
  const attachProblems = useMemo(() => {
    const problems: string[] = [];

    chosen.forEach((choice) => {
      const item = byId.get(choice.item);
      const device = choice.device ?? batchDevice;
      const label = item?.key || `item ${choice.item}`;

      if (device === null) {
        problems.push(`${label} has no device. Pick one for the batch, or one for this row.`);
        return;
      }

      const target = deviceById.get(device);

      if (!target) return;

      // Both sides have to claim a type for this to mean anything. An untyped item goes anywhere,
      // which is the point of leaving it unset.
      if (
        item?.device_type &&
        target.device_type &&
        item.device_type !== target.device_type
      ) {
        problems.push(
          `${label} is written for ${
            item.device_type_name || item.device_type_key || 'another device type'
          }, and ${target.device_key} is ${
            target.device_type_name || target.device_type_key || 'a different type'
          }.`,
        );
      }
    });

    return problems;
  }, [chosen, batchDevice, byId, deviceById]);

  /** The pairs that will actually be sent, duplicates dropped. */
  const sendable = useMemo(
    () => chosen.filter((choice) => !alreadyThere.includes(choice)),
    [chosen, alreadyThere],
  );

  const clearResults = () => {
    setAttachResult(null);
    setDetachResult(null);
    setCompileResult(null);
    setActionError(null);
  };

  const runAttach = async () => {
    if (!scenarioId || !sendable.length) return;

    if (attachProblems.length) {
      setActionError(attachProblems[0]);
      return;
    }

    clearResults();

    const pairs: LabItemAttachPair[] = sendable.map((choice) => ({
      item: choice.item,
      device: (choice.device ?? batchDevice) as number,
    }));

    try {
      const result = await attach.mutateAsync({ scenarioId, items: pairs });
      setAttachResult(result);
      setChosen([]);
      setAttachOpen(false);
    } catch (exc) {
      setActionError(labErrorMessage(exc, 'Could not attach those items.'));
    }
  };

  /**
   * Opens the attach form with the batch device already chosen when there is only one.
   *
   * One device and a select asking which is noise. More than one is left unchosen: which box an item
   * lands on is the decision, and defaulting to the first would make it silently.
   */
  const toggleAttachForm = () => {
    const opening = !attachOpen;

    setAttachOpen(opening);
    setChosen([]);
    setBatchDevice(opening && sortedDevices.length === 1 ? sortedDevices[0].id : null);
  };

  const runDetach = async (attachment: LabScenarioItem) => {
    if (!scenarioId) return;

    const label = attachment.item_name || attachment.item_key || `item ${attachment.item}`;

    // eslint-disable-next-line no-restricted-globals
    if (
      !window.confirm(
        `Detach ${label}? Prerequisites it pulled in that nothing else needs go with it. The steps it compiled stay until the next compile, which removes them - or switches them off if a run has already been scored against them.`,
      )
    ) {
      return;
    }

    clearResults();

    try {
      setDetachResult(await detach.mutateAsync({ scenarioId, attachmentId: attachment.id }));
    } catch (exc) {
      setActionError(labErrorMessage(exc, 'Could not detach that item.'));
    }
  };

  const runCompile = async () => {
    if (!scenarioId) return;

    clearResults();

    try {
      setCompileResult(await compile.mutateAsync(scenarioId));
    } catch (exc) {
      setActionError(
        labErrorMessage(
          exc,
          'The compile was refused and nothing was written. Fix what it named and run it again.',
        ),
      );
    }
  };

  const openOverrides = (attachment: LabScenarioItem) => {
    if (expanded === attachment.id) {
      setExpanded(null);
      return;
    }

    setExpanded(attachment.id);
    setOverrideDraft({
      id: attachment.id,
      option_overrides: attachment.option_overrides || {},
      enabled: attachment.enabled,
      notes: attachment.notes || '',
    });
    setOverrideError(null);
  };

  const saveOverrides = async () => {
    if (overrideError) return;

    setActionError(null);

    try {
      await saveAttachment.mutateAsync(overrideDraft as Partial<LabScenarioItem> & { id: number });
      setExpanded(null);
    } catch (exc) {
      setActionError(labErrorMessage(exc, 'Could not save those option values.'));
    }
  };

  const compileNeeded = !!(attachResult?.compile_required || detachResult?.compile_required);

  return (
    <LabCard
      title={`Configuration items (${rows.length})`}
      subtitle={
        scenarioName
          ? `What ${scenarioName} is made of. The steps are what the last compile produced from this list.`
          : 'What this scenario is made of. The steps are what the last compile produced from this list.'
      }
      actions={
        canEdit && scenarioId ? (
          <>
            <StbButton
              role="add"
              size="small"
              disabled={!sortedDevices.length}
              onClick={async () => toggleAttachForm()}
            >
              {attachOpen ? 'Cancel' : 'Attach items'}
            </StbButton>
            <StbButton
              role="default"
              size="small"
              loading={compile.isPending}
              disabled={!rows.length}
              onClick={async () => runCompile()}
            >
              Compile into steps
            </StbButton>
          </>
        ) : undefined
      }
    >
      {!scenarioId ? (
        <p className="m-0 text-sm text-gray-600">
          Pick a scenario first. Items are attached to a scenario, and the scenario is what a lab
          runs.
        </p>
      ) : (
        <>
          {actionError && <p className="m-0 mb-3 text-sm text-amber-800">{actionError}</p>}

          {/* Said plainly rather than left to be noticed: an attach or a detach changes what the
              lab is made of and nothing about what it will run until this is pressed. */}
          {compileNeeded && (
            <div className="mb-3 rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
              The item list has changed and the steps have not. Compile to bring them into line -
              until then this lab still runs the previous configuration.
            </div>
          )}

          {attachResult && (
            <div className="mb-3 rounded border border-gray-200 bg-gray-50 p-3 text-sm">
              <div className="font-medium">
                Attached {attachResult.attached.length}, {attachResult.total} items in total.
              </div>
              {/* The whole reason the endpoint reports this. Eleven rows appearing after one
                  choice is a bug until it is a list of prerequisites. */}
              {!!attachResult.added_as_prerequisites.length && (
                <div className="mt-1 text-xs text-gray-600">
                  Also added because something needed them:{' '}
                  <span className="font-mono">
                    {attachResult.added_as_prerequisites.join(', ')}
                  </span>
                </div>
              )}
              {!!attachResult.promoted.length && (
                <div className="mt-1 text-xs text-gray-600">
                  Already here as prerequisites, now chosen directly:{' '}
                  <span className="font-mono">{attachResult.promoted.join(', ')}</span>
                </div>
              )}
            </div>
          )}

          {detachResult && (
            <div className="mb-3 rounded border border-gray-200 bg-gray-50 p-3 text-sm">
              <div className="font-medium">
                Detached <span className="font-mono">{detachResult.detached.join(', ')}</span>.
              </div>
              {!!detachResult.pruned.length && (
                <div className="mt-1 text-xs text-gray-600">
                  Prerequisites removed with it, because nothing left needed them:{' '}
                  <span className="font-mono">{detachResult.pruned.join(', ')}</span>
                </div>
              )}
            </div>
          )}

          {compileResult && (
            <div className="mb-3 rounded border border-gray-200 bg-gray-50 p-3 text-sm">
              <div className="font-medium">
                Compiled {compileResult.items} items into{' '}
                {compileResult.steps_created.length + compileResult.steps_updated} steps and{' '}
                {compileResult.criteria_created.length + compileResult.criteria_updated} criteria.
              </div>

              <dl className="m-0 mt-2 grid grid-cols-1 gap-x-6 gap-y-1 text-xs sm:grid-cols-2">
                <div>
                  <dt className="inline text-gray-500">New steps: </dt>
                  <dd className="m-0 inline font-mono">
                    {compileResult.steps_created.join(', ') || 'none'}
                  </dd>
                </div>
                <div>
                  <dt className="inline text-gray-500">New criteria: </dt>
                  <dd className="m-0 inline font-mono">
                    {compileResult.criteria_created.join(', ') || 'none'}
                  </dd>
                </div>
                {!!compileResult.monitored_devices.length && (
                  <div className="sm:col-span-2">
                    <dt className="inline text-gray-500">Now monitored: </dt>
                    <dd className="m-0 inline font-mono">
                      {compileResult.monitored_devices.join(', ')}
                    </dd>
                  </div>
                )}
              </dl>

              {/* Disabled rather than deleted is not tidying: a run was scored against those
                  rows, so they stay and stop running. Worth reading, not worth hiding. */}
              {(!!compileResult.steps_disabled.length ||
                !!compileResult.criteria_disabled.length) && (
                <div className="mt-2 text-xs text-gray-600">
                  Kept but switched off, because a run has already been scored against them:{' '}
                  <span className="font-mono">
                    {[...compileResult.steps_disabled, ...compileResult.criteria_disabled].join(', ')}
                  </span>
                </div>
              )}

              {(!!compileResult.steps_deleted.length || !!compileResult.criteria_deleted.length) && (
                <div className="mt-1 text-xs text-gray-600">
                  Removed, unused by any run:{' '}
                  <span className="font-mono">
                    {[...compileResult.steps_deleted, ...compileResult.criteria_deleted].join(', ')}
                  </span>
                </div>
              )}

              {/* Warnings, not errors: every one of these describes something that *will* run,
                  just not the way the author probably meant. Anything unrunnable rolls back. */}
              {!!compileResult.warnings.length && (
                <div className="mt-2 rounded border border-amber-300 bg-amber-50 p-2 text-amber-900">
                  <div className="text-xs font-medium">
                    This compiled, and these are what to look at:
                  </div>
                  <ul className="mb-0 mt-1 list-disc pl-5 text-xs">
                    {compileResult.warnings.map((warning) => (
                      <li key={warning}>{warning}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Nothing to attach anything to. Said here rather than by a disabled button with no
              reason, because it is the normal state of a scenario nobody has added devices to. */}
          {!sortedDevices.length && (
            <p className="m-0 mb-3 text-sm text-amber-800">
              This scenario has no devices, and an attachment is an item on a device. Add one on the
              Devices &amp; steps tab first.
            </p>
          )}

          {attachOpen && (
            <div className="mb-4 rounded-md border border-gray-200 bg-gray-50 p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-sm font-semibold text-gray-700">
                  Pick from the catalogue ({available.length} items)
                </span>
                <StbButton
                  role="add"
                  size="small"
                  loading={attach.isPending}
                  disabled={!sendable.length || !!attachProblems.length}
                  onClick={async () => runAttach()}
                >
                  {sendable.length
                    ? `Attach ${sendable.length + impliedIds.length}`
                    : 'Attach'}
                </StbButton>
              </div>

              {/* One choice for the whole batch, overridable per row below. Eight items onto the
                  firewall and one onto the jump host is the shape this work has. */}
              <div className="mt-3 max-w-sm">
                <LabRowSelect
                  label="Device for this batch"
                  value={batchDevice}
                  rows={sortedDevices}
                  onChange={setBatchDevice}
                  labelFor={(device) =>
                    `${device.device_key}${device.display_name ? ` - ${device.display_name}` : ''}`
                  }
                  hintFor={(device) =>
                    device.device_type_name || device.device_type_key || 'No device type set'
                  }
                  emptyLabel="Pick a device"
                  helpText="Every item ticked below goes here unless its own row says otherwise. Prerequisites pulled in follow whatever needed them."
                />
              </div>

              {!available.length ? (
                <p className="m-0 mt-2 text-xs text-gray-500">
                  The catalogue is empty. Author an item before assembling a lab from it.
                </p>
              ) : (
                <div className="mt-2 max-h-80 overflow-y-auto rounded border border-gray-200 bg-white">
                  {available.map((item) => {
                    const blockers = labItemBlockers(item);
                    const choice = chosen.find((entry) => entry.item === item.id) || null;
                    const onDevices = attachedDevicesByItem.get(item.id) || [];
                    const target = choice ? deviceById.get(choice.device ?? batchDevice ?? -1) : null;
                    const duplicate = !!choice && alreadyThere.includes(choice);
                    const mismatch =
                      !!target &&
                      !!item.device_type &&
                      !!target.device_type &&
                      item.device_type !== target.device_type;

                    return (
                      <div
                        key={item.id}
                        className="flex flex-wrap items-start gap-2 border-b border-gray-100 px-2 py-1.5 text-sm last:border-b-0"
                      >
                        <label className="flex flex-1 items-start gap-2 hover:bg-gray-50">
                          <input
                            type="checkbox"
                            className="mt-1"
                            checked={!!choice}
                            onChange={() =>
                              setChosen((current) =>
                                current.some((entry) => entry.item === item.id)
                                  ? current.filter((entry) => entry.item !== item.id)
                                  : [...current, { item: item.id, device: null }],
                              )
                            }
                          />
                          <span className="flex-1">
                            <span className="font-medium">{item.name}</span>
                            <span className="ml-2 font-mono text-xs text-gray-500">{item.key}</span>
                            <span className="block text-xs text-gray-500">
                              {labItemOperationLabel(item.operation)}
                              {item.device_type
                                ? ` • for ${item.device_type_name || item.device_type_key}`
                                : ' • any device type'}
                              {item.pre_configure ? ' • already in the template' : ''}
                              {(item.prerequisite_keys || []).length
                                ? ` • needs ${(item.prerequisite_keys || []).join(', ')}`
                                : ''}
                            </span>

                            {/* Already on a device is information, not an error - the next one is
                                usually the point of picking it again. */}
                            {!!onDevices.length && (
                              <span className="block text-xs text-gray-500">
                                Already on {onDevices.join(', ')}
                              </span>
                            )}
                            {duplicate && (
                              <span className="block text-xs text-gray-500">
                                Already on that device, so this row will be skipped.
                              </span>
                            )}
                            {mismatch && (
                              <span className="block text-xs text-amber-800">
                                Written for {item.device_type_name || item.device_type_key}, and{' '}
                                {target?.device_key} is{' '}
                                {target?.device_type_name || target?.device_type_key}.
                              </span>
                            )}
                            {!!blockers.length && (
                              <span className="mt-0.5 block text-xs text-amber-800">
                                {blockers[0]}
                              </span>
                            )}
                          </span>
                        </label>

                        {/* Only once a row is ticked: an unticked row is not going anywhere, and a
                            select per catalogue row would be a wall of them. */}
                        {!!choice && (
                          <div className="min-w-[200px]">
                            <LabRowSelect
                              label="On"
                              value={choice.device}
                              rows={sortedDevices}
                              onChange={(device) =>
                                setChosen((current) =>
                                  current.map((entry) =>
                                    entry.item === item.id ? { ...entry, device } : entry,
                                  ),
                                )
                              }
                              labelFor={(device) => device.device_key}
                              emptyLabel={
                                batchDevice
                                  ? `Batch device (${deviceById.get(batchDevice)?.device_key})`
                                  : 'The batch device'
                              }
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Before the request, not after it. The server walks the same graph, so this is a
                  preview of its answer rather than a second opinion. */}
              {!!impliedIds.length && (
                <p className="m-0 mt-2 text-xs text-gray-600">
                  These come with them, because they are needed first:{' '}
                  <span className="font-mono">
                    {impliedIds.map((id) => byId.get(id)?.key || `item ${id}`).join(', ')}
                  </span>
                  . Each lands on the device of whatever required it.
                </p>
              )}

              {!!attachProblems.length && (
                <ul className="mb-0 mt-2 list-disc pl-5 text-xs text-amber-800">
                  {attachProblems.map((problem) => (
                    <li key={problem}>{problem}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <LabPanelState
            loading={isLoading}
            error={error}
            empty={!rows.length}
            emptyMessage="Nothing is attached, so this scenario compiles to nothing and its labs come up as the bare dCloud template."
            loadingLabel="Loading attached items..."
          >
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
                    <th className="px-3 py-2 font-semibold">#</th>
                    <th className="px-3 py-2 font-semibold">Item</th>
                    <th className="px-3 py-2 font-semibold">On device</th>
                    <th className="px-3 py-2 font-semibold">Operation</th>
                    <th className="px-3 py-2 font-semibold">Options in effect</th>
                    <th className="px-3 py-2 font-semibold">Why it is here</th>
                    <th className="px-3 py-2 font-semibold text-right">Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {rows.map((attachment, index) => {
                    const item = byId.get(attachment.item) || null;
                    const options =
                      attachment.effective_options || labEffectiveOptions(item, attachment);
                    const overridden = Object.keys(attachment.option_overrides || {});
                    const open = expanded === attachment.id;

                    return (
                      <React.Fragment key={attachment.id}>
                        <tr className="border-b border-gray-100 align-top">
                          <td className="px-3 py-2 text-xs text-gray-500">{index + 1}</td>

                          <td className="px-3 py-2">
                            <div className="font-medium">
                              {attachment.item_name || item?.name || `item ${attachment.item}`}
                            </div>
                            <div className="font-mono text-xs text-gray-500">
                              {attachment.item_key || item?.key}
                            </div>

                            <div className="mt-1 flex flex-wrap gap-1">
                              {!attachment.enabled && (
                                <LabStatusTag label="Not compiled" sentiment="warning" />
                              )}
                              {attachment.item_enabled === false && (
                                <LabStatusTag label="Item disabled" sentiment="warning" />
                              )}
                              {(attachment.item_pre_configure ?? item?.pre_configure) && (
                                <LabStatusTag label="Pre-built" sentiment="info" />
                              )}
                            </div>
                          </td>

                          {/* The other half of what an attachment is. A mismatch here is what the
                              attach refused, so seeing it means the type moved afterwards. */}
                          <td className="px-3 py-2 whitespace-nowrap">
                            <div className="font-mono text-xs">
                              {attachment.device_key ||
                                deviceById.get(attachment.device)?.device_key ||
                                `device ${attachment.device}`}
                            </div>
                            <div className="text-xs text-gray-500">
                              {attachment.device_type_key || 'no device type'}
                            </div>
                            {attachment.item_device_type_key &&
                              attachment.device_type_key &&
                              attachment.item_device_type_key !== attachment.device_type_key && (
                                <div className="text-xs text-amber-800">
                                  The item is written for {attachment.item_device_type_key}.
                                </div>
                              )}
                          </td>

                          <td className="px-3 py-2 whitespace-nowrap">
                            {labItemOperationLabel(attachment.item_operation || item?.operation)}
                          </td>

                          <td className="px-3 py-2">
                            {Object.keys(options).length ? (
                              <LabJsonView value={options} className="max-h-32" />
                            ) : (
                              <span className="text-xs text-gray-500">No options</span>
                            )}
                            {!!overridden.length && (
                              <div className="mt-1 text-xs text-gray-500">
                                Set for this lab: <span className="font-mono">{overridden.join(', ')}</span>
                              </div>
                            )}
                          </td>

                          <td className="px-3 py-2 text-xs">
                            {attachment.added_as_prerequisite ? (
                              <span className="text-gray-600">
                                Needed by{' '}
                                <span className="font-mono">
                                  {attachment.required_by_key || 'another item'}
                                </span>
                              </span>
                            ) : (
                              <span className="text-gray-600">Chosen for this lab</span>
                            )}
                            {attachment.notes && (
                              <div className="mt-1 text-gray-500">{attachment.notes}</div>
                            )}
                          </td>

                          <td className="px-3 py-2">
                            <div className="flex flex-wrap items-center justify-end gap-2">
                              {canEdit && (
                                <>
                                  <StbButton
                                    role="update"
                                    size="xsmall"
                                    onClick={async () => openOverrides(attachment)}
                                  >
                                    {open ? 'Close' : 'Options'}
                                  </StbButton>
                                  <StbButton
                                    role="delete"
                                    size="xsmall"
                                    loading={detach.isPending}
                                    onClick={async () => runDetach(attachment)}
                                  >
                                    Detach
                                  </StbButton>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>

                        {open && (
                          <tr className="border-b border-gray-100 bg-gray-50">
                            <td className="px-3 py-3" colSpan={7}>
                              <div className="flex flex-col gap-3">
                                {/* Only the overrides are editable. The defaults belong to the
                                    catalogue, and editing them from here would change every
                                    other lab that attached the same item. */}
                                <LabSpecBuilder
                                  schema={LAB_JSON_SCHEMAS.attachmentOptionOverrides}
                                  value={overrideDraft.option_overrides}
                                  resetKey={attachment.id}
                                  context={{ optionKeys: Object.keys(item?.options || {}) }}
                                  onChange={(option_overrides, jsonError) => {
                                    setOverrideError(jsonError);
                                    if (!jsonError) {
                                      setOverrideDraft((current) => ({
                                        ...current,
                                        option_overrides,
                                      }));
                                    }
                                  }}
                                >
                                  {item && !Object.keys(item.options || {}).length && (
                                    <p className="m-0 text-xs text-gray-500">
                                      This item declares no options, so anything set here is carried
                                      and never substituted into a step.
                                    </p>
                                  )}
                                </LabSpecBuilder>

                                <LabCheckbox
                                  label="Include in the compile"
                                  checked={!!overrideDraft.enabled}
                                  onChange={(enabled) =>
                                    setOverrideDraft((current) => ({ ...current, enabled }))
                                  }
                                  helpText="Clearing this leaves the item attached with its option values and compiles nothing from it - which is how a step is taken out of a lab without losing what it was set to."
                                />

                                <TextField
                                  label="Notes"
                                  value={overrideDraft.notes || ''}
                                  onChange={(evt) =>
                                    setOverrideDraft((current) => ({
                                      ...current,
                                      notes: evt.target.value,
                                    }))
                                  }
                                  helperText="Why this item is in this lab, or why its options differ from the defaults."
                                  multiline
                                  minRows={2}
                                  fullWidth
                                />

                                <div className="flex flex-wrap items-center gap-2">
                                  <StbButton
                                    role="update"
                                    size="small"
                                    loading={saveAttachment.isPending}
                                    disabled={!!overrideError}
                                    onClick={async () => saveOverrides()}
                                  >
                                    Save
                                  </StbButton>
                                  <span className="text-xs text-gray-500">
                                    Saved values are substituted at the next compile, not now.
                                  </span>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </LabPanelState>
        </>
      )}
    </LabCard>
  );
};

export default LabScenarioItemsPanel;
